import { arrow, autoUpdate, flip, offset, shift, size, useFloating } from "@floating-ui/react-dom";
import { compact } from "lodash";

export type FloatingDropdownParams = {
  alignment?: "start" | "end";
  maximize?: boolean;
  disableOffset?: boolean;
  disabled?: boolean;
};

export function useFloatingDropdown({
  alignment = "start",
  maximize,
  disableOffset,
}: FloatingDropdownParams = {}) {
  return useFloating({
    placement: alignment === "start" ? "bottom-start" : "bottom-end",
    whileElementsMounted: autoUpdate,
    middleware: compact([
      !disableOffset && offset(3),
      shift(),
      size({
        apply: ({ elements, rects, availableWidth, availableHeight }) => {
          if (maximize) {
            elements.floating.style.width = `${availableWidth}px`;
            elements.floating.style.height = `${availableHeight}px`;
          } else {
            elements.floating.style.minWidth = `${rects.reference.width}px`;
            elements.floating.style.maxWidth = `${availableWidth}px`;
            elements.floating.style.maxHeight = `${Math.max(200, availableHeight)}px`;
          }
        },
      }),
      flip({ crossAxis: false }),
    ]),
  });
}

export type FloatingPopoverParams = {
  open: boolean;
  targetEl: HTMLElement | null;
  popoverEl: HTMLElement | null;
  arrowEl?: HTMLElement | null;
  autoUpdate?: boolean;
  placement?: "top" | "bottom";
};

export function useFloatingPopover({
  open,
  targetEl,
  popoverEl,
  arrowEl,
  placement = "bottom",
  ...props
}: FloatingPopoverParams) {
  return useFloating({
    open,
    placement,
    elements: {
      reference: targetEl,
      floating: popoverEl,
    },
    whileElementsMounted: props.autoUpdate ? autoUpdate : undefined,
    middleware: [
      offset(8),
      flip(),
      shift(),
      size({
        apply: ({ elements, availableWidth }) => {
          elements.floating.style.maxWidth = `min(${availableWidth}px, max-content)`;
        },
      }),
      arrowEl !== undefined && arrow({ element: arrowEl }),
    ],
  });
}
